/*
 * 🃏 DFCL — Dragonious Flame Card Legends landing (Arcade tab)
 * -------------------------------------------------------------
 * The HOME of the DFCL card fighting game inside the Arcade. Right
 * now, this tab holds:
 *   • The owner-canon Rules button (opens the full rulebook overlay)
 *   • A hero header with the card-back and a one-line pitch
 *   • A "Duel UI — coming soon" placeholder (no gameplay yet, per
 *     owner directive 2026-10-02)
 *   • A quick link to the Card Collection (sibling tab)
 *
 * This is INTENTIONALLY separate from the Card Collection (DragoniousCCG).
 * Collection = Pokédex-style gallery. DFCL = the fighting game.
 * They cross-link but are different rooms.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Printer, Swords, Library, ExternalLink, Layers, Check, Lock } from "lucide-react";
import DFCLRulebook from "@/components/DFCLRulebook";
import { useAuth } from "@/contexts/AuthContext";
import { DFCL_STARTER_DECKS, ownsDeck } from "@/data/dfclStarterDecks";
import { scrubWorkingNotes, scrubCardNote } from "@/utils/scrubWorkingNotes";

// 🎨 Section tone → tailwind class map. Keeps card-type palette aligned
// with the rulebook legend (DragoniousCCG CLASS_RIBBON) so the deck
// preview reads instantly even without labels.
const TONE_CLASSES = {
  emerald: { border: "border-emerald-500/40", dot: "bg-emerald-500",  text: "text-emerald-300" },
  zinc:    { border: "border-zinc-500/40",    dot: "bg-zinc-400",     text: "text-zinc-300" },
  slate:   { border: "border-slate-500/40",   dot: "bg-slate-400",    text: "text-slate-300" },
  red:     { border: "border-red-500/40",     dot: "bg-red-500",      text: "text-red-300" },
  indigo:  { border: "border-indigo-500/40",  dot: "bg-indigo-500",   text: "text-indigo-300" },
  amber:   { border: "border-amber-500/40",   dot: "bg-amber-500",    text: "text-amber-300" },
};

// 🎴 Owner-supplied card-back icon (golden back · Dragonious purple/red/black).
const CARD_BACK_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/" +
  "job_omni-brand-1/artifacts/b6q7tkhp_Approved%20card%20back.webp";

export default function DragoniousDFCL({ onJumpToTab }) {
  const { user } = useAuth();
  const [showRules, setShowRules] = useState(false);
  // Discovery-first: ALL decks appear in the list, but locked ones
  // render as "???" stubs — only the Welcome deck shows its contents
  // out of the box. Players learn the roster by unlocking.
  const decks = DFCL_STARTER_DECKS.map((d) => ({ ...d, owned: ownsDeck(d, user) }));
  const ownedCount  = decks.filter((d) => d.owned).length;
  const lockedCount = decks.length - ownedCount;

  return (
    <div className="text-neutral-200" data-testid="dfcl-landing">
      {/* ─── Hero header ─────────────────────────────────────────── */}
      <div className="border border-amber-500/40 bg-gradient-to-br from-[#1a0808] via-[#120410] to-black p-5 sm:p-7 mb-5">
        <div className="flex items-start gap-5 flex-wrap">
          <img
            src={CARD_BACK_URL}
            alt="Dragonious Flame Card Legends — card back"
            className="w-24 sm:w-32 border border-amber-500/60 shadow-[0_0_30px_rgba(217,119,6,0.4)]"
            draggable={false}
          />
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
              🔥 Dragonious
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-white mt-1 leading-tight">
              Flame Card Legends
            </h2>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl leading-snug">
              A 2-player strategic card fighting game. Build a 50-card deck,
              field Fighters, trigger Storylines, and finish with a Showtime
              or Fatality before your opponent's Life Points hit zero.
            </p>
            {/* Primary CTAs — rules + print, with duel locked for now */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => setShowRules(true)}
                data-testid="dfcl-open-rulebook"
                className="inline-flex items-center gap-2 border border-amber-500/60 bg-gradient-to-br from-amber-950/40 to-black hover:border-amber-400 hover:bg-amber-950/60 text-amber-200 px-4 py-2 transition-colors"
              >
                <BookOpen size={16} className="text-amber-400" />
                <span className="font-accent text-xs tracking-[0.3em] uppercase">
                  Official Rules
                </span>
              </button>
              <Link
                to="/arcade/rules/dfcl"
                data-testid="dfcl-rulebook-deeplink"
                className="inline-flex items-center gap-2 border border-zinc-700 bg-black hover:border-amber-400 hover:text-amber-200 text-neutral-300 px-4 py-2 transition-colors"
                title="Open the shareable rulebook page in a new context"
              >
                <ExternalLink size={14} />
                <span className="font-accent text-xs tracking-[0.3em] uppercase">
                  Open as Page
                </span>
              </Link>
              <button
                onClick={() => window.print()}
                data-testid="dfcl-print-shortcut"
                className="inline-flex items-center gap-2 border border-zinc-700 bg-black hover:border-amber-400 hover:text-amber-200 text-neutral-400 px-4 py-2 transition-colors"
                title="Print or save as PDF (uses the rules print styles)"
              >
                <Printer size={14} />
                <span className="font-accent text-xs tracking-[0.3em] uppercase">
                  Export PDF
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Deck Grid — owned decks preview, locked decks masked ── */}
      <div className="mb-5" data-testid="dfcl-deck-grid">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
            Decks · {ownedCount}/{decks.length}
          </div>
          {lockedCount > 0 && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-1.5" data-testid="dfcl-locked-count">
              <Lock size={11} className="text-neutral-500" />
              {lockedCount} deck{lockedCount === 1 ? "" : "s"} locked — win, find, or buy
            </div>
          )}
        </div>
        {decks.map((d) => (
          d.owned
            ? <StarterDeckPreview key={d.id} deck={d} />
            : <LockedDeckStub key={d.id} deck={d} />
        ))}
      </div>

      {/* ─── Duel UI — placeholder ───────────────────────────────── */}
      <div
        className="border border-dashed border-red-500/40 bg-black/40 p-5 sm:p-7 mb-5 text-center"
        data-testid="dfcl-duel-placeholder"
      >
        <Swords size={32} className="mx-auto text-red-400/80 mb-2" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 font-bold">
          Duel UI — Coming Soon
        </div>
        <h3 className="font-display text-xl text-white mt-1">
          Face NPC Duelists
        </h3>
        <p className="text-sm text-neutral-400 mt-2 max-w-md mx-auto leading-snug">
          Three NPCs are already tagged as duelists in the realm —
          Grandma Cari, Chef David Lauer, and Nathaniel Bucket. Walk up to
          them to see the <span className="text-amber-300">🃏 DFCL Duelist</span> badge
          in their dialogue. The actual duel system drops when the rules are
          wired to a battle engine.
        </p>
      </div>

      {/* ─── Cross-link to Collection ───────────────────────────── */}
      <div className="border border-zinc-800 bg-black/40 p-4 flex items-center gap-4 flex-wrap">
        <Library size={22} className="text-amber-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-semibold">Looking for the Card Collection?</div>
          <div className="text-[11px] text-neutral-400 leading-snug">
            Every canon Fighter, Skill, Showtime, Fatality, and Storyline card lives in the <strong>Cards</strong> tab — collect them all. Many are future DFCL deck fodder.
          </div>
        </div>
        <button
          onClick={() => onJumpToTab?.("ccg")}
          data-testid="dfcl-jump-to-cards"
          className="inline-flex items-center gap-2 border border-zinc-700 hover:border-amber-400 hover:text-amber-200 text-neutral-300 px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors"
        >
          Open Cards →
        </button>
      </div>

      {/* 📖 Rulebook overlay */}
      {showRules && <DFCLRulebook onClose={() => setShowRules(false)} />}
    </div>
  );
}

function LockedDeckStub({ deck }) {
  // Discovery-first: mirror the Card Collection's locked-card treatment.
  // Show the slot (so players know an expansion exists) but reveal
  // nothing — no name, no hero number, no card list, no strategy.
  return (
    <div
      className="relative border border-dashed border-zinc-700/70 bg-black/40 p-5 sm:p-7 mb-5 overflow-hidden"
      data-testid={`dfcl-locked-deck-${deck.id}`}
    >
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(217,119,6,0.3) 0px, rgba(217,119,6,0.3) 1px, transparent 1px, transparent 10px)",
        }}
      />
      <div className="relative flex items-center gap-4 flex-wrap">
        <div className="flex items-center justify-center w-14 h-20 border border-zinc-700 bg-black/60 flex-shrink-0">
          <Lock size={22} className="text-neutral-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-neutral-600 font-bold">
            <Layers size={12} className="text-neutral-600" />
            Starter Deck · ??
          </div>
          <h3 className="font-display text-2xl text-neutral-500 mt-1 leading-tight select-none">
            ??? ???
          </h3>
          <p className="text-[12px] text-neutral-600 italic mt-2 max-w-xl leading-snug">
            Locked — win, find, or buy to reveal.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Starter Deck Preview ─────────────────────────────────────── */
/* Read-only showcase of a legal 50-card Series-1 build so players
   can study a correct deck shape before gameplay ships. Data is
   verbatim from `/data/dfclStarterDecks.js` (Roc 2026-09-17). */
function StarterDeckPreview({ deck }) {
  if (!deck) return null;
  const m = deck.math || {};
  return (
    <div
      className="border border-amber-500/30 bg-black/40 p-5 sm:p-7 mb-5"
      data-testid="dfcl-starter-deck"
    >
      {/* Header + math badges */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5 border-b border-zinc-900 pb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
            <Layers size={14} className="text-amber-400" />
            Starter Deck · {String(deck.order).padStart(2, "0")}
          </div>
          <h3 className="font-display text-2xl text-white mt-1 leading-tight">
            {deck.name}
          </h3>
          {/* 🧹 Working-note scrubber (standing rule 2026-10-29): drop
              approval dates, "filed by Roc", "[S1✗]" tags, etc. from
              every player-facing deck string. Deck sizes/counts stay. */}
          {scrubWorkingNotes(deck.strategy) && (
            <p className="text-[12px] text-neutral-400 mt-2 max-w-2xl leading-snug">
              {scrubWorkingNotes(deck.strategy)}
            </p>
          )}
          {scrubWorkingNotes(deck.legality) && (
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
              {scrubWorkingNotes(deck.legality)}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0" data-testid="deck-math">
          <MathBadge label="Main"     value={`${m.main_deck?.current}/${m.main_deck?.cap}`} ok={m.main_deck?.current === m.main_deck?.cap} />
          {/* 🟨 Finisher zone — if the deck's Finisher is still pending
              (count=0 + pending_note), show amber "PENDING" instead of
              a red "out of range" so the badge reads as awaiting-canon,
              not broken. */}
          {deck.finisher?.pending_note ? (
            <MathBadge label="Finisher" value="PENDING" pending />
          ) : (
            <MathBadge label="Finisher" value={`${m.finisher?.current}/${m.finisher?.min}–${m.finisher?.max}`} ok={(m.finisher?.current || 0) >= (m.finisher?.min || 0) && (m.finisher?.current || 0) <= (m.finisher?.max || 10)} />
          )}
          <MathBadge label="Fighters" value={`${m.fighters?.current || 0}`} ok={(m.fighters?.current || 0) >= (m.fighters?.min || 1)} />
          <MathBadge label="Copies"   value="≤ 3 ea." ok={!!m.copy_limit_ok} />
        </div>
      </div>

      {/* Main deck sections */}
      <div className="mb-4 text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
        Main Deck ({deck.math?.main_deck?.current || 50})
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        {deck.main.map((sec) => (
          <DeckSection key={sec.section} sec={sec} />
        ))}
      </div>

      {/* Finisher Zone */}
      <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
        Finisher Zone ({deck.finisher.count})
      </div>
      {deck.finisher.cards.length > 0 ? (
        <DeckSection sec={{ ...deck.finisher, section: "Finishers" }} standalone />
      ) : (
        <div
          className="border border-dashed border-amber-500/30 bg-black/40 p-4 text-center"
          data-testid="deck-finisher-pending"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/80 font-bold">
            Coming Soon
          </div>
          <div className="text-[12px] text-neutral-400 italic mt-1 leading-snug">
            Finisher Zone coming soon.
          </div>
        </div>
      )}

      {/* Engine Lines block hidden on the player view (standing rule
          2026-10-29: no ENGINE LINES design commentary). The field
          still exists in the source data for admin / rulebook use. */}
    </div>
  );
}

function MathBadge({ label, value, ok, pending }) {
  const style = pending
    ? "border-amber-500/50 text-amber-300 bg-amber-950/20"
    : ok
      ? "border-emerald-500/50 text-emerald-300 bg-emerald-950/20"
      : "border-red-500/50 text-red-300 bg-red-950/20";
  return (
    <div
      data-testid={`deck-math-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[10px] uppercase tracking-widest ${style}`}
    >
      {ok && !pending && <Check size={10} className="text-emerald-400" />}
      {pending && <Lock size={9} className="text-amber-400" />}
      <span className="text-neutral-500">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

function DeckSection({ sec, standalone = false }) {
  const tone = TONE_CLASSES[sec.tone] || TONE_CLASSES.zinc;
  return (
    <div
      className={`border ${tone.border} bg-black/40 p-3 ${standalone ? "" : ""}`}
      data-testid={`deck-section-${sec.section.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-zinc-900">
        <span className={`${tone.dot} w-2.5 h-2.5 rounded-full`} />
        <span className={`font-accent text-[11px] tracking-widest uppercase ${tone.text} font-bold`}>
          {sec.section}
        </span>
        <span className="text-neutral-500 text-[10px] uppercase tracking-widest ml-auto">
          {sec.count} card{sec.count === 1 ? "" : "s"}
        </span>
      </div>
      <ul className="space-y-1">
        {sec.cards.map((c) => {
          const safeNote = scrubCardNote(c.note);
          return (
            <li
              key={c.name}
              className="flex items-start gap-2 text-[12px] leading-snug"
            >
              <span className="font-mono tabular-nums text-neutral-500 text-[11px] shrink-0 w-6 text-right">
                ×{c.qty}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-neutral-200">{c.name}</span>
                {safeNote && (
                  <span className="text-neutral-500 italic ml-1">— {safeNote}</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
